import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, Modal, ProgressBar, Row, Spinner } from "react-bootstrap"
import useFetch from "../../../_fetch"
import TravelArticleWorkflow from "./travel-article-workflow"

const ARTICLE_TYPES = [
	{
		value: "product-comparison",
		label: "Produktsammenligning",
	},
	{
		value: "editorial-guide",
		label: "Redaktionel guide",
	},
	{
		value: "store-guide",
		label: "Butiksguide",
	},
	{
		value: "travel-article",
		label: "Rejseartikel",
	},
]

const ARTICLE_LENGTH_OPTIONS = [
	{ value: "short", label: "Kort" },
	{ value: "normal", label: "Normal" },
	{ value: "long", label: "Lang" },
]

const WP_SITE = "https://pristjek.ekstrabladet.dk"
const HISTORY_WINDOW_MONTHS = 3

type ArticleDraftResponse = {
	articleId?: number
	mode?: string
	articleTypeName?: string
	previewLink?: string
	editLink?: string
	request?: {
		title?: string
		topic?: string
	}
	draft?: {
		title?: string
		topic?: string
		productSections?: ArticlePreviewProduct[]
		storeSections?: ArticlePreviewStore[]
		productSearchMeta?: {
			rejectedCandidates?: ArticlePreviewProduct[]
		}
		storeSearchMeta?: {
			rejectedCandidates?: ArticlePreviewStore[]
		}
	}
	createdPost?: {
		id?: number
	}
}

type ArticlePreviewProduct = {
	sectionTitle?: string
	productTitle?: string
	ean?: string
	productId?: number
	imageUrl?: string
	internalProductUrl?: string
	selectionReason?: string
	discoverySource?: "suggested" | "candidate" | "manual-search"
}

type ArticlePreviewStore = {
	storeName?: string
	shopUrl?: string
	domain?: string
	network?: string
	selectionReason?: string
	discoverySource?: "suggested" | "candidate" | "manual-search"
}

type PreviewSelectionItem = {
	id: string
	group: "product" | "store"
	label: string
	description?: string
	raw: ArticlePreviewProduct | ArticlePreviewStore
	discoverySource?: "suggested" | "candidate" | "manual-search"
}

type ArticleCandidateSearchResponse = {
	entityType?: "product" | "store"
	items?: Array<ArticlePreviewProduct | ArticlePreviewStore>
	error?: string
}

type CreatedArticleLinks = {
	articleId: number
	previewLink: string
	editLink: string
	title: string
	topic: string
	articleTypeName: string
	status?: string
	date?: string
	dateGmt?: string
	updatedAt?: string
	scheduledPublishAt?: string
	publishedAt?: string
	publicLink?: string
}

type ArticleInputLink = {
	href: string
	text: string
}

type RecentArticlesResponse = {
	items?: CreatedArticleLinks[]
}

type DiscoveryPreview = {
	title: string
	topic: string
	articleType: string
	items: PreviewSelectionItem[]
}

type ProgressStep = {
	afterMs: number
	progress: number
	label: string
	description: string
}

function formatElapsedSeconds(elapsedMs: number) {
	return `${Math.max(1, Math.round(elapsedMs / 1000))} sek`
}

function getOperationSteps(articleType: string, mode: "discover" | "submit", { singleOfferMode = false }: { singleOfferMode?: boolean } = {}): ProgressStep[] {
	if (mode === "submit") {
		return [
			{ afterMs: 0, progress: 12, label: "Forbereder artikel", description: "Vi samler titel, valg og prompt til det endelige kladdeudkast." },
			{ afterMs: 8000, progress: 34, label: "Genererer indhold", description: "AI bygger artikelens struktur og brødtekst ud fra dine valgte produkter eller butikker." },
			{ afterMs: 22000, progress: 58, label: "Finjusterer indhold", description: "Indhold, titel og SEO-felter bliver justeret og kvalitetssikret." },
			{ afterMs: 38000, progress: 78, label: "Opretter WordPress-kladde", description: "Kladde, billede og metadata bliver sendt til WordPress." },
			{ afterMs: 52000, progress: 92, label: "Afslutter oprettelse", description: "De sidste artikeldata og specialfelter bliver gjort klar." },
		]
	}

	if (articleType === "store-guide") {
		return [
			{ afterMs: 0, progress: 10, label: "Forstår prompten", description: "Vi udleder emne og artikelvinkel fra din prompt." },
			{ afterMs: 7000, progress: 42, label: "Henter butikker", description: "Alle aktive butikker gøres klar som valgmuligheder." },
			{ afterMs: 18000, progress: 76, label: "Bygger forslag", description: "Titel, topic og butiksliste gøres klar til manuel gennemgang." },
		]
	}

	if (articleType === "editorial-guide") {
		return [
			{ afterMs: 0, progress: 10, label: "Forstår prompten", description: "Vi udleder titel og artikelvinkel fra din prompt." },
			{ afterMs: 7000, progress: 38, label: "Henter produkter", description: "De 50 senest oprettede produkter gøres klar som valgmuligheder." },
			{ afterMs: 16000, progress: 68, label: "Henter butikker", description: "Alle aktive butikker gøres klar som valgmuligheder." },
			{ afterMs: 26000, progress: 90, label: "Bygger forslag", description: "Titel, topic og valglister gøres klar til manuel gennemgang." },
		]
	}

	if (articleType === "product-comparison" && singleOfferMode) {
		return [
			{ afterMs: 0, progress: 10, label: "Forstår vinklen", description: "Vi udleder titel og retning fra din prompt." },
			{ afterMs: 7000, progress: 48, label: "Henter produkter", description: "De 50 senest oprettede produkter gøres klar som valgmuligheder." },
			{ afterMs: 18000, progress: 88, label: "Bygger forslag", description: "Titel, topic og produktliste gøres klar til manuel gennemgang." },
		]
	}

	return [
		{ afterMs: 0, progress: 10, label: "Forstår prompten", description: "Vi udleder emne og artikelretning fra din prompt." },
		{ afterMs: 7000, progress: 48, label: "Henter produkter", description: "De 50 senest oprettede produkter gøres klar som valgmuligheder." },
		{ afterMs: 18000, progress: 90, label: "Bygger forslag", description: "Titel, topic og produktliste gøres klar til manuel gennemgang." },
	]
}

function isProductPreviewItem(item: PreviewSelectionItem) {
	return item.group === "product"
}

function isStorePreviewItem(item: PreviewSelectionItem) {
	return item.group === "store"
}

function getArticleTypeLabel(value: string) {
	return ARTICLE_TYPES.find((type) => type.value === value)?.label || ARTICLE_TYPES[0].label
}

function buildArticleLinks(articleId: number) {
	return {
		previewLink: `${WP_SITE}/?p=${articleId}&preview=true&no_redirect=1`,
		editLink: `${WP_SITE}/wp-admin/post.php?post=${articleId}&action=edit`,
	}
}

function buildProductPreviewItem(item: ArticlePreviewProduct, discoverySource: "suggested" | "candidate" | "manual-search" = "suggested"): PreviewSelectionItem {
	const productIdentifier = String(item.ean || "").trim() || (Number(item.productId || 0) ? `woo:${Number(item.productId || 0)}` : "")
	return {
		id: `product:${productIdentifier}`,
		group: "product",
		label: String(item.productTitle || item.sectionTitle || "Produkt"),
		description: "",
		raw: { ...item, discoverySource },
		discoverySource,
	}
}

function buildStorePreviewItem(item: ArticlePreviewStore, discoverySource: "suggested" | "candidate" | "manual-search" = "suggested"): PreviewSelectionItem {
	return {
		id: `store:${String(item.shopUrl || "").trim()}|${String(item.storeName || "").trim()}`,
		group: "store",
		label: String(item.storeName || item.domain || "Butik"),
		description: [item.domain, item.network].filter(Boolean).join(" · "),
		raw: { ...item, discoverySource },
		discoverySource,
	}
}

function getPreviewIdentityKey(item: PreviewSelectionItem) {
	if (item.group === "product") {
		const product = item.raw as ArticlePreviewProduct
		const productIdentifier = String(product?.ean || "").trim() || (Number(product?.productId || 0) ? `woo:${Number(product.productId || 0)}` : "")
		return `product:${productIdentifier}`
	}
	return `store:${String((item.raw as ArticlePreviewStore)?.shopUrl || "").trim()}|${String((item.raw as ArticlePreviewStore)?.storeName || "").trim().toLowerCase()}`
}

function getSelectionItemDescription(item: PreviewSelectionItem) {
	if (item.group === "product") return ""
	return String(item.description || "").trim()
}

function readFileAsDataUrl(file: File) {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => resolve(String(reader.result || ""))
		reader.onerror = () => reject(new Error("Kunne ikke læse billedfilen"))
		reader.readAsDataURL(file)
	})
}

function createEmptyArticleInputLink(): ArticleInputLink {
	return {
		href: "",
		text: "",
	}
}

function normalizeArticleLinksForRequest(links: ArticleInputLink[]) {
	return (Array.isArray(links) ? links : [])
		.map((link) => ({
			href: String(link?.href || "").trim(),
			text: String(link?.text || "").trim(),
			maxUses: 2,
		}))
		.filter((link) => Boolean(link.href && link.text))
}

const selectionListContainerStyle = {
	maxHeight: "420px",
	overflowY: "auto" as const,
	paddingRight: "0.25rem",
}

const selectionItemStyle = {
	cursor: "pointer",
	padding: "0.55rem 0.75rem",
}

const selectionLabelStyle = {
	whiteSpace: "normal" as const,
	overflowWrap: "anywhere" as const,
	wordBreak: "break-word" as const,
	lineHeight: 1.3,
}

const selectionPanelStyle = {
	minHeight: "100%",
}

const plannerListContainerStyle = {
	maxHeight: "760px",
	overflowY: "auto" as const,
	paddingRight: "0.25rem",
}

const plannerViewSwitcherStyle = {
	display: "grid",
	gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
	gap: "0.75rem",
}

const plannerViewButtonBaseStyle = {
	borderRadius: "16px",
	border: "1px solid #d7dce2",
	padding: "0.9rem 1rem",
	textAlign: "left" as const,
	background: "#f6f8fb",
	transition: "all 0.18s ease",
	boxShadow: "none",
}

const plannerViewButtonActiveStyle = {
	background: "#ffffff",
	border: "1px solid #212529",
	boxShadow: "0 12px 28px rgba(33, 37, 41, 0.08)",
}

const plannerToolbarStyle = {
	border: "1px solid #e7ebf0",
	borderRadius: "18px",
	padding: "0.9rem 1rem",
	background: "linear-gradient(180deg, #fafbfd 0%, #f4f7fa 100%)",
}

const calendarToolbarStyle = {
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	gap: "0.75rem",
	marginBottom: "1rem",
	padding: "0.7rem 0.85rem",
	borderRadius: "18px",
	border: "1px solid #e6ebf2",
	background: "#f7f9fc",
}

const calendarMonthLabelStyle = {
	fontWeight: 700,
	fontSize: "0.95rem",
	letterSpacing: "0.01em",
	textTransform: "capitalize" as const,
}

const calendarNavButtonStyle = {
	width: "36px",
	height: "36px",
	borderRadius: "999px",
	padding: 0,
	fontSize: "1rem",
	fontWeight: 700,
	lineHeight: 1,
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
}

const calendarGridStyle = {
	display: "grid",
	gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
	gap: "0.75rem",
}

const calendarCellBaseStyle = {
	minHeight: "96px",
	borderRadius: "18px",
	border: "1px solid #e6ebf2",
	padding: "0.7rem",
	background: "#ffffff",
	boxShadow: "0 6px 18px rgba(15, 23, 42, 0.04)",
	display: "flex",
	flexDirection: "column" as const,
	gap: "0.35rem",
}

const calendarCellMutedStyle = {
	background: "#f7f8fa",
	border: "1px solid #edf1f5",
	boxShadow: "none",
	opacity: 0.72,
}

const calendarCellTodayStyle = {
	border: "1px solid #212529",
	boxShadow: "0 10px 30px rgba(33, 37, 41, 0.08)",
}

const calendarCellSelectedStyle = {
	border: "1px solid #2f6fed",
	boxShadow: "0 14px 36px rgba(47, 111, 237, 0.16)",
	background: "#ffffff",
}

const calendarCellTodaySelectedStyle = {
	border: "1px solid #212529",
	boxShadow: "0 0 0 3px rgba(47, 111, 237, 0.2), 0 14px 36px rgba(33, 37, 41, 0.12)",
	background: "#ffffff",
}

const calendarCellExpandedStyle = {
	minHeight: "220px",
	gap: "0.55rem",
}

const calendarExpandedGridItemStyle = {
	gridColumn: "span 2",
}

const calendarDayMetaStyle = {
	fontSize: "0.74rem",
	color: "#6c757d",
	lineHeight: 1.25,
}

const calendarDotRowStyle = {
	display: "flex",
	alignItems: "center",
	gap: "0.35rem",
	marginTop: "auto",
	flexWrap: "wrap" as const,
}

const calendarDotBaseStyle = {
	width: "8px",
	height: "8px",
	borderRadius: "999px",
	display: "inline-block",
}

const calendarExpandedListStyle = {
	display: "flex",
	flexDirection: "column" as const,
	gap: "0.45rem",
}

const calendarExpandedArticleButtonStyle = {
	padding: "0.45rem 0.55rem",
	borderRadius: "12px",
	border: "1px solid #e3e9f0",
	background: "#f8fafc",
	lineHeight: 1.2,
	width: "100%",
}

function formatPlannerDate(value?: string, includeWeekday = false) {
	if (!value) return "-"
	const parsed = new Date(value)
	if (Number.isNaN(parsed.getTime())) return "-"
	return parsed.toLocaleString("da-DK", {
		weekday: includeWeekday ? "short" : undefined,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	})
}

function formatPlannerTime(value?: string) {
	if (!value) return "-"
	const parsed = new Date(value)
	if (Number.isNaN(parsed.getTime())) return "-"
	return parsed.toLocaleTimeString("da-DK", {
		hour: "2-digit",
		minute: "2-digit",
	})
}

function getPlannerTimestamp(item: CreatedArticleLinks) {
	return item.scheduledPublishAt || item.publishedAt || item.date || item.dateGmt || item.updatedAt || ""
}

function getPlannerStatusLabel(status?: string) {
	switch (String(status || "").trim()) {
		case "future":
			return "Planlagt"
		case "publish":
			return "Publiceret"
		case "draft":
		default:
			return "Kladde"
	}
}

function getPlannerStatusVariant(status?: string): "secondary" | "warning" | "success" {
	switch (String(status || "").trim()) {
		case "future":
			return "warning"
		case "publish":
			return "success"
		case "draft":
		default:
			return "secondary"
	}
}

function getCalendarStatusDotColor(status?: string) {
	switch (String(status || "").trim()) {
		case "future":
			return "#d4a017"
		case "publish":
			return "#198754"
		default:
			return "#adb5bd"
	}
}

function toDateTimeLocalValue(value?: string) {
	if (!value) return ""
	const parsed = new Date(value)
	if (Number.isNaN(parsed.getTime())) return ""
	const year = parsed.getFullYear()
	const month = String(parsed.getMonth() + 1).padStart(2, "0")
	const day = String(parsed.getDate()).padStart(2, "0")
	const hours = String(parsed.getHours()).padStart(2, "0")
	const minutes = String(parsed.getMinutes()).padStart(2, "0")
	return `${year}-${month}-${day}T${hours}:${minutes}`
}

function getArticlePrimaryDateLabel(item: CreatedArticleLinks) {
	if (item.status === "future" && item.scheduledPublishAt) return `Planlagt: ${formatPlannerDate(item.scheduledPublishAt, true)}`
	if (item.status === "publish" && item.publishedAt) return `Publiceret: ${formatPlannerDate(item.publishedAt, true)}`
	if (item.date || item.dateGmt) return `Oprettet: ${formatPlannerDate(item.date || item.dateGmt, true)}`
	return "Ingen dato"
}

function getCalendarArticleTimeLabel(item: CreatedArticleLinks) {
	if (item.status === "future" && item.scheduledPublishAt) return `Planlagt kl. ${formatPlannerTime(item.scheduledPublishAt)}`
	if (item.status === "publish" && item.publishedAt) return `Publiceret kl. ${formatPlannerTime(item.publishedAt)}`
	const timestamp = getPlannerTimestamp(item)
	return timestamp ? `Tidspunkt kl. ${formatPlannerTime(timestamp)}` : ""
}

function canShowPreviewLink(item?: CreatedArticleLinks | null) {
	return String(item?.status || "").trim() !== "publish" && Boolean(String(item?.previewLink || "").trim())
}

function canShowPublicLink(item?: CreatedArticleLinks | null) {
	return String(item?.status || "").trim() === "publish" && Boolean(String(item?.publicLink || "").trim())
}

function canCopyPublicLink(item?: CreatedArticleLinks | null) {
	const status = String(item?.status || "").trim()
	return (status === "future" || status === "publish") && Boolean(String(item?.publicLink || "").trim())
}

function startOfCalendarMonth(value: Date) {
	return new Date(value.getFullYear(), value.getMonth(), 1)
}

function buildCalendarDays(viewMonth: Date) {
	const start = startOfCalendarMonth(viewMonth)
	const end = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0)
	const gridStart = new Date(start)
	const gridEnd = new Date(end)
	const weekDay = (start.getDay() + 6) % 7
	gridStart.setDate(start.getDate() - weekDay)
	const endWeekDay = (end.getDay() + 6) % 7
	gridEnd.setDate(end.getDate() + (6 - endWeekDay))
	const dayCount = Math.round((gridEnd.getTime() - gridStart.getTime()) / (24 * 60 * 60 * 1000)) + 1

	return Array.from({ length: dayCount }, (_, index) => {
		const date = new Date(gridStart)
		date.setDate(gridStart.getDate() + index)
		return date
	})
}

function getCalendarKey(value: Date) {
	return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`
}

function getItemCalendarKey(item: CreatedArticleLinks) {
	const timestamp = getPlannerTimestamp(item)
	if (!timestamp) return ""
	const parsed = new Date(timestamp)
	if (Number.isNaN(parsed.getTime())) return ""
	return getCalendarKey(parsed)
}

function isCalendarStatus(item: CreatedArticleLinks) {
	const status = String(item.status || "").trim()
	return status === "future" || status === "publish"
}

export default function ArticleTab() {
	const { doFetch } = useFetch()
	const [articleType, setArticleType] = useState("editorial-guide")
	const [singleOfferMode, setSingleOfferMode] = useState(false)
	const [articleLength, setArticleLength] = useState("short")
	const [discoverOptimized, setDiscoverOptimized] = useState(false)
	const [prompt, setPrompt] = useState("")
	const [imageDataUrl, setImageDataUrl] = useState("")
	const [imageFileName, setImageFileName] = useState("")
	const [isDiscovering, setIsDiscovering] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [error, setError] = useState("")
	const [preview, setPreview] = useState<DiscoveryPreview | null>(null)
	const [editableTitle, setEditableTitle] = useState("")
	const [selectedPreviewIds, setSelectedPreviewIds] = useState<string[]>([])
	const [createdArticle, setCreatedArticle] = useState<CreatedArticleLinks | null>(null)
	const [articleHistory, setArticleHistory] = useState<CreatedArticleLinks[]>([])
	const [isLoadingHistory, setIsLoadingHistory] = useState(true)
	const [historyError, setHistoryError] = useState("")
	const [plannerView, setPlannerView] = useState<"list" | "calendar">("list")
	const [plannerFilter, setPlannerFilter] = useState<"all" | "draft" | "future" | "publish">("all")
	const [scheduleTarget, setScheduleTarget] = useState<CreatedArticleLinks | null>(null)
	const [scheduleDateValue, setScheduleDateValue] = useState("")
	const [scheduleError, setScheduleError] = useState("")
	const [isScheduling, setIsScheduling] = useState(false)
	const [copiedArticleId, setCopiedArticleId] = useState<number | null>(null)
	const [copyErrorArticleId, setCopyErrorArticleId] = useState<number | null>(null)
	const [calendarMonth, setCalendarMonth] = useState(() => startOfCalendarMonth(new Date()))
	const [selectedCalendarDayKey, setSelectedCalendarDayKey] = useState(() => getCalendarKey(new Date()))
	const [activeOperation, setActiveOperation] = useState<"discover" | "submit" | null>(null)
	const [operationStartedAt, setOperationStartedAt] = useState<number | null>(null)
	const [operationElapsedMs, setOperationElapsedMs] = useState(0)
	const [manualProductSearchOpen, setManualProductSearchOpen] = useState(false)
	const [manualProductSearchQuery, setManualProductSearchQuery] = useState("")
	const [manualProductSearchResults, setManualProductSearchResults] = useState<PreviewSelectionItem[]>([])
	const [manualProductSearchError, setManualProductSearchError] = useState("")
	const [isManualProductSearchLoading, setIsManualProductSearchLoading] = useState(false)
	const [manualStoreSearchOpen, setManualStoreSearchOpen] = useState(false)
	const [manualStoreSearchQuery, setManualStoreSearchQuery] = useState("")
	const [manualStoreSearchResults, setManualStoreSearchResults] = useState<PreviewSelectionItem[]>([])
	const [manualStoreSearchError, setManualStoreSearchError] = useState("")
	const [isManualStoreSearchLoading, setIsManualStoreSearchLoading] = useState(false)
	const [articleLinks, setArticleLinks] = useState<ArticleInputLink[]>([createEmptyArticleInputLink()])

	const resetManualSearch = useCallback(() => {
		setManualProductSearchOpen(false)
		setManualProductSearchQuery("")
		setManualProductSearchResults([])
		setManualProductSearchError("")
		setIsManualProductSearchLoading(false)
		setManualStoreSearchOpen(false)
		setManualStoreSearchQuery("")
		setManualStoreSearchResults([])
		setManualStoreSearchError("")
		setIsManualStoreSearchLoading(false)
	}, [])

	const canDiscover = useMemo(() => {
		return Boolean(articleType && prompt.trim() && imageDataUrl)
	}, [articleType, prompt, imageDataUrl])

	const isSingleOfferProductComparison = useMemo(() => {
		return articleType === "product-comparison" && singleOfferMode
	}, [articleType, singleOfferMode])

	const selectedPreviewItems = useMemo(() => {
		if (!preview) return []
		return preview.items.filter((item) => selectedPreviewIds.includes(item.id))
	}, [preview, selectedPreviewIds])

	const candidatePreviewItems = useMemo(() => {
		if (!preview) return []
		return preview.items.filter((item) => !selectedPreviewIds.includes(item.id))
	}, [preview, selectedPreviewIds])

	const selectedProducts = useMemo(() => {
		return selectedPreviewItems.filter(isProductPreviewItem).map((item) => item.raw as ArticlePreviewProduct)
	}, [selectedPreviewItems])

	const selectedStores = useMemo(() => {
		return selectedPreviewItems.filter(isStorePreviewItem).map((item) => item.raw as ArticlePreviewStore)
	}, [selectedPreviewItems])

	const selectedProductItems = useMemo(() => selectedPreviewItems.filter(isProductPreviewItem), [selectedPreviewItems])
	const selectedStoreItems = useMemo(() => selectedPreviewItems.filter(isStorePreviewItem), [selectedPreviewItems])
	const candidateProductItems = useMemo(() => candidatePreviewItems.filter(isProductPreviewItem), [candidatePreviewItems])
	const candidateStoreItems = useMemo(() => candidatePreviewItems.filter(isStorePreviewItem), [candidatePreviewItems])
	const visibleManualProductSearchResults = useMemo(() => {
		return manualProductSearchResults
	}, [manualProductSearchResults])
	const visibleManualStoreSearchResults = useMemo(() => {
		return manualStoreSearchResults
	}, [manualStoreSearchResults])
	const isManualProductSearchActive = useMemo(() => {
		return manualProductSearchOpen && Boolean(manualProductSearchError || isManualProductSearchLoading || manualProductSearchResults.length)
	}, [manualProductSearchError, isManualProductSearchLoading, manualProductSearchOpen, manualProductSearchResults.length])
	const isManualStoreSearchActive = useMemo(() => {
		return manualStoreSearchOpen && Boolean(manualStoreSearchError || isManualStoreSearchLoading || manualStoreSearchResults.length)
	}, [isManualStoreSearchLoading, manualStoreSearchError, manualStoreSearchOpen, manualStoreSearchResults.length])
	const displayedStoreCandidateItems = useMemo(() => {
		return isManualStoreSearchActive ? visibleManualStoreSearchResults : candidateStoreItems
	}, [candidateStoreItems, isManualStoreSearchActive, visibleManualStoreSearchResults])
	const displayedProductCandidateItems = useMemo(() => {
		return isManualProductSearchActive ? visibleManualProductSearchResults : candidateProductItems
	}, [candidateProductItems, isManualProductSearchActive, visibleManualProductSearchResults])
	const shouldShowStoreCandidateSection = useMemo(() => {
		return manualStoreSearchOpen || isManualStoreSearchLoading || Boolean(manualStoreSearchError) || displayedStoreCandidateItems.length > 0
	}, [displayedStoreCandidateItems.length, isManualStoreSearchLoading, manualStoreSearchError, manualStoreSearchOpen])
	const shouldShowProductCandidateSection = useMemo(() => {
		return manualProductSearchOpen || isManualProductSearchLoading || Boolean(manualProductSearchError) || displayedProductCandidateItems.length > 0
	}, [displayedProductCandidateItems.length, isManualProductSearchLoading, manualProductSearchError, manualProductSearchOpen])

	const canSubmit = useMemo(() => {
		if (!preview || isDiscovering) return false
		if (preview.articleType === "editorial-guide") return selectedPreviewItems.length >= 1
		if (preview.articleType === "product-comparison") return selectedPreviewItems.length >= 1
		return selectedPreviewItems.length >= 1
	}, [preview, selectedPreviewItems.length, isDiscovering])

	const operationStatus = useMemo(() => {
		if (!activeOperation || !operationStartedAt) return null
		const steps = getOperationSteps(articleType, activeOperation, { singleOfferMode })
		const currentStep = [...steps].reverse().find((step) => operationElapsedMs >= step.afterMs) || steps[0]
		return {
			mode: activeOperation,
			elapsedLabel: formatElapsedSeconds(operationElapsedMs),
			steps,
			currentStep,
		}
	}, [activeOperation, articleType, operationElapsedMs, operationStartedAt, singleOfferMode])

	const loadArticleHistory = useCallback(async () => {
		setIsLoadingHistory(true)
		setHistoryError("")

		try {
			const response = await doFetch(`/eb-pristjek/articles?limit=400&statuses=draft,future,publish&afterMonths=${HISTORY_WINDOW_MONTHS}`)
			const data = await response.json() as RecentArticlesResponse | { error?: string }

			if (response.status >= 400) {
				throw new Error(String((data as { error?: string })?.error || "Kunne ikke hente artikeloversigten."))
			}

			setArticleHistory(Array.isArray((data as RecentArticlesResponse).items) ? (data as RecentArticlesResponse).items || [] : [])
		} catch (err) {
			console.error("Fejl ved hentning af artikeloversigt:", err)
			setHistoryError(err instanceof Error ? err.message : "Kunne ikke hente artikeloversigten.")
		} finally {
			setIsLoadingHistory(false)
		}
	}, [doFetch])

	const filteredArticleHistory = useMemo(() => {
		if (plannerFilter === "all") return articleHistory
		return articleHistory.filter((item) => item.status === plannerFilter)
	}, [articleHistory, plannerFilter])

	const calendarArticleHistory = useMemo(() => {
		return articleHistory.filter(isCalendarStatus)
	}, [articleHistory])

	const plannerCounts = useMemo(() => {
		return articleHistory.reduce((acc, item) => {
			const status = String(item.status || "draft").trim()
			if (status === "future") acc.future += 1
			else if (status === "publish") acc.publish += 1
			else acc.draft += 1
			return acc
		}, { draft: 0, future: 0, publish: 0 })
	}, [articleHistory])

	const calendarDays = useMemo(() => buildCalendarDays(calendarMonth), [calendarMonth])
	const todayCalendarKey = useMemo(() => getCalendarKey(new Date()), [])
	const calendarItemsByDay = useMemo(() => {
		const map = new Map<string, CreatedArticleLinks[]>()
		for (const item of calendarArticleHistory) {
			const key = getItemCalendarKey(item)
			if (!key) continue
			const items = map.get(key) || []
			items.push(item)
			items.sort((left, right) => {
				const leftTime = Date.parse(getPlannerTimestamp(left)) || 0
				const rightTime = Date.parse(getPlannerTimestamp(right)) || 0
				return leftTime - rightTime
			})
			map.set(key, items)
		}
		return map
	}, [calendarArticleHistory])

	useEffect(() => {
		if (!activeOperation || !operationStartedAt) {
			setOperationElapsedMs(0)
			return
		}

		setOperationElapsedMs(Date.now() - operationStartedAt)
		const interval = window.setInterval(() => {
			setOperationElapsedMs(Date.now() - operationStartedAt)
		}, 500)

		return () => window.clearInterval(interval)
	}, [activeOperation, operationStartedAt])

	useEffect(() => {
		loadArticleHistory()
	}, [loadArticleHistory])

	useEffect(() => {
		if (!selectedCalendarDayKey) return
		const visibleKeys = new Set(calendarDays.map((day) => getCalendarKey(day)))
		if (visibleKeys.has(selectedCalendarDayKey)) return
		const fallbackDay = calendarDays.find((day) => day.getMonth() === calendarMonth.getMonth()) || calendarDays[0]
		if (fallbackDay) setSelectedCalendarDayKey(getCalendarKey(fallbackDay))
	}, [calendarDays, calendarMonth, selectedCalendarDayKey])

	const handleCalendarDayToggle = (dayKey: string) => {
		setSelectedCalendarDayKey((current) => current === dayKey ? "" : dayKey)
	}

	const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) {
			setImageDataUrl("")
			setImageFileName("")
			setPreview(null)
			setEditableTitle("")
			setSelectedPreviewIds([])
			return
		}

		if (!String(file.type || "").startsWith("image/")) {
			setError("Vælg en gyldig billedfil.")
			return
		}

		if (file.size > 8 * 1024 * 1024) {
			setError("Billedfilen er for stor. Maks størrelse er 8 MB.")
			return
		}

		try {
			setError("")
			const dataUrl = await readFileAsDataUrl(file)
			setImageDataUrl(dataUrl)
			setImageFileName(file.name)
			setPreview(null)
			setEditableTitle("")
			setSelectedPreviewIds([])
			resetManualSearch()
		} catch (err) {
			console.error("Fejl ved billedupload:", err)
			setError("Kunne ikke læse billedfilen.")
		}
	}

	const handleDiscover = async () => {
		if (!canDiscover) return

		setActiveOperation("discover")
		setOperationStartedAt(Date.now())
		setIsDiscovering(true)
		setError("")
		setCreatedArticle(null)
		resetManualSearch()

		try {
			const normalizedArticleLinks = normalizeArticleLinksForRequest(articleLinks)
			const response = await doFetch("/eb-pristjek/articles/drafts", {
				articleType,
				singleOfferMode: articleType === "product-comparison" ? singleOfferMode : false,
				articleLength,
				aiDescription: prompt.trim(),
				customPrompt: prompt.trim(),
				featuredImageUrl: imageDataUrl,
				articleLinks: normalizedArticleLinks,
				discoverOptimized,
				discoverOnly: true,
			})

			const data = await response.json() as ArticleDraftResponse | { error?: string }
			if (response.status >= 400) {
				throw new Error(String((data as { error?: string })?.error || "Kunne ikke finde forslag til artiklen."))
			}

			const previewProducts = Array.isArray((data as ArticleDraftResponse).draft?.productSections)
				? (data as ArticleDraftResponse).draft?.productSections || []
				: []
			const rejectedProducts = Array.isArray((data as ArticleDraftResponse).draft?.productSearchMeta?.rejectedCandidates)
				? (data as ArticleDraftResponse).draft?.productSearchMeta?.rejectedCandidates || []
				: []
			const previewStores = Array.isArray((data as ArticleDraftResponse).draft?.storeSections)
				? (data as ArticleDraftResponse).draft?.storeSections || []
				: []
			const rejectedStores = Array.isArray((data as ArticleDraftResponse).draft?.storeSearchMeta?.rejectedCandidates)
				? (data as ArticleDraftResponse).draft?.storeSearchMeta?.rejectedCandidates || []
				: []

			let nextItems: PreviewSelectionItem[] = []
			let initiallySelectedIds: string[] = []

			if (articleType === "store-guide") {
				nextItems = [
					...previewStores.map((item) => ({ ...item, discoverySource: "suggested" as const })),
					...rejectedStores.map((item) => ({ ...item, discoverySource: "candidate" as const })),
				]
					.filter((item) => Boolean(String(item.shopUrl || "").trim()))
					.filter((item, index, array) => {
						const key = `${String(item.shopUrl || "").trim()}|${String(item.storeName || "").trim()}`
						return index === array.findIndex((candidate) => `${String(candidate.shopUrl || "").trim()}|${String(candidate.storeName || "").trim()}` === key)
					})
					.map((item) => buildStorePreviewItem(item, item.discoverySource))

				initiallySelectedIds = []
			} else if (articleType === "editorial-guide") {
				const productItems = [
					...previewProducts.map((item) => ({ ...item, discoverySource: "suggested" as const })),
					...rejectedProducts.map((item) => ({ ...item, discoverySource: "candidate" as const })),
				]
					.filter((item) => Boolean(String(item.ean || "").trim() || Number(item.productId || 0)))
					.filter((item, index, array) => {
						const key = String(item.ean || "").trim() || `woo:${Number(item.productId || 0)}`
						return index === array.findIndex((candidate) => (String(candidate.ean || "").trim() || `woo:${Number(candidate.productId || 0)}`) === key)
					})
					.map((item) => buildProductPreviewItem(item, item.discoverySource))

				const storeItems = [
					...previewStores.map((item) => ({ ...item, discoverySource: "suggested" as const })),
					...rejectedStores.map((item) => ({ ...item, discoverySource: "candidate" as const })),
				]
					.filter((item) => Boolean(String(item.shopUrl || "").trim()))
					.filter((item, index, array) => {
						const key = `${String(item.shopUrl || "").trim()}|${String(item.storeName || "").trim()}`
						return index === array.findIndex((candidate) => `${String(candidate.shopUrl || "").trim()}|${String(candidate.storeName || "").trim()}` === key)
					})
					.map((item) => buildStorePreviewItem(item, item.discoverySource))

				nextItems = [...productItems, ...storeItems]
				initiallySelectedIds = []
			} else {
				nextItems = [
					...previewProducts.map((item) => ({ ...item, discoverySource: "suggested" as const })),
					...rejectedProducts.map((item) => ({ ...item, discoverySource: "candidate" as const })),
				]
					.filter((item) => Boolean(String(item.ean || "").trim() || Number(item.productId || 0)))
					.filter((item, index, array) => {
						const key = String(item.ean || "").trim() || `woo:${Number(item.productId || 0)}`
						return index === array.findIndex((candidate) => (String(candidate.ean || "").trim() || `woo:${Number(candidate.productId || 0)}`) === key)
					})
					.map((item) => ({ ...buildProductPreviewItem(item, item.discoverySource), description: "" }))

				initiallySelectedIds = []
			}

			const nextPreview = {
				title: String((data as ArticleDraftResponse).draft?.title || (data as ArticleDraftResponse).request?.title || ""),
				topic: String((data as ArticleDraftResponse).draft?.topic || (data as ArticleDraftResponse).request?.topic || ""),
				articleType,
				items: nextItems,
			}

			setPreview(nextPreview)
			setEditableTitle(nextPreview.title)
			setSelectedPreviewIds(initiallySelectedIds)
		} catch (err) {
			console.error("Fejl ved artikel-discovery:", err)
			setPreview(null)
			setEditableTitle("")
			setSelectedPreviewIds([])
			setError(err instanceof Error ? err.message : "Kunne ikke finde forslag til artiklen.")
		} finally {
			setIsDiscovering(false)
			setActiveOperation(null)
			setOperationStartedAt(null)
		}
	}

	const handleStartOver = () => {
		setPreview(null)
		setEditableTitle("")
		setSelectedPreviewIds([])
		setError("")
		resetManualSearch()
	}

	const togglePreviewSelection = (id: string) => {
		setSelectedPreviewIds((prev) => {
			if (prev.includes(id)) {
				return prev.filter((item) => item !== id)
			}
			return [...prev, id]
		})
	}

	const toggleCandidateSelection = (item: PreviewSelectionItem) => {
		if (selectedPreviewIds.includes(item.id)) {
			togglePreviewSelection(item.id)
			return
		}

		setPreview((current) => {
			if (!current) return current
			if (current.items.some((existing) => getPreviewIdentityKey(existing) === getPreviewIdentityKey(item))) return current
			return {
				...current,
				items: [...current.items, item],
			}
		})
		togglePreviewSelection(item.id)
	}

	const handleManualCandidateSearch = async (entityType: "product" | "store") => {
		const query = entityType === "product" ? manualProductSearchQuery.trim() : manualStoreSearchQuery.trim()
		if (!query) return
		if (entityType === "product") {
			setIsManualProductSearchLoading(true)
			setManualProductSearchError("")
		} else {
			setIsManualStoreSearchLoading(true)
			setManualStoreSearchError("")
		}
		try {
			const response = await doFetch("/eb-pristjek/articles/candidates/search", {
				entityType,
				query,
				limit: entityType === "product" ? 20 : 8,
				articleType,
			})
			const data = await response.json() as ArticleCandidateSearchResponse
			if (response.status >= 400) {
				throw new Error(String(data?.error || "Kunne ikke søge efter kandidater."))
			}
			const existingByIdentity = new Map((preview?.items || []).map((item) => [getPreviewIdentityKey(item), item]))
			const incomingItems = entityType === "product"
				? (Array.isArray(data?.items) ? data.items as ArticlePreviewProduct[] : []).map((item) => buildProductPreviewItem(item, "manual-search"))
				: (Array.isArray(data?.items) ? data.items as ArticlePreviewStore[] : []).map((item) => buildStorePreviewItem(item, "manual-search"))
			const nextItems = incomingItems.map((item) => existingByIdentity.get(getPreviewIdentityKey(item)) || item)
			if (entityType === "product") {
				setManualProductSearchResults(nextItems)
				if (!nextItems.length) setManualProductSearchError("Ingen produkter matchede søgningen.")
			} else {
				setManualStoreSearchResults(nextItems)
				if (!nextItems.length) setManualStoreSearchError("Ingen butikker matchede søgningen.")
			}
		} catch (err) {
			if (entityType === "product") {
				setManualProductSearchResults([])
				setManualProductSearchError(err instanceof Error ? err.message : "Kunne ikke søge efter produkter.")
			} else {
				setManualStoreSearchResults([])
				setManualStoreSearchError(err instanceof Error ? err.message : "Kunne ikke søge efter butikker.")
			}
		} finally {
			if (entityType === "product") setIsManualProductSearchLoading(false)
			else setIsManualStoreSearchLoading(false)
		}
	}

	const resetManualCandidateSearch = (entityType: "product" | "store") => {
		if (entityType === "product") {
			setManualProductSearchOpen(false)
			setManualProductSearchQuery("")
			setManualProductSearchResults([])
			setManualProductSearchError("")
			setIsManualProductSearchLoading(false)
			return
		}
		setManualStoreSearchOpen(false)
		setManualStoreSearchQuery("")
		setManualStoreSearchResults([])
		setManualStoreSearchError("")
		setIsManualStoreSearchLoading(false)
	}

	const openScheduleModal = (item: CreatedArticleLinks) => {
		setScheduleTarget(item)
		setScheduleDateValue(toDateTimeLocalValue(item.scheduledPublishAt || item.publishedAt || item.date || item.dateGmt))
		setScheduleError("")
	}

	const closeScheduleModal = () => {
		if (isScheduling) return
		setScheduleTarget(null)
		setScheduleDateValue("")
		setScheduleError("")
	}

	const handleCopyPublicLink = async (item: CreatedArticleLinks) => {
		const publicLink = String(item.publicLink || "").trim()
		if (!publicLink) return

		try {
			await navigator.clipboard.writeText(publicLink)
			setCopiedArticleId(item.articleId)
			setCopyErrorArticleId(null)
		} catch (err) {
			console.error("Fejl ved kopiering af artikel-link:", err)
			setCopiedArticleId(null)
			setCopyErrorArticleId(item.articleId)
		}
	}

	const isScheduleReadOnly = String(scheduleTarget?.status || "").trim() === "publish"

	const updateArticleInState = (updatedItem: CreatedArticleLinks) => {
		setArticleHistory((prev) => {
			const next = [updatedItem, ...prev.filter((item) => item.articleId !== updatedItem.articleId)]
			return next.sort((left, right) => (Date.parse(getPlannerTimestamp(right)) || 0) - (Date.parse(getPlannerTimestamp(left)) || 0))
		})
		setCreatedArticle((prev) => prev?.articleId === updatedItem.articleId ? updatedItem : prev)
	}

	const handleScheduleSubmit = async () => {
		if (!scheduleTarget) return
		if (!scheduleDateValue) {
			setScheduleError("Vælg dato og klokkeslæt.")
			return
		}

		setIsScheduling(true)
		setScheduleError("")

		try {
			const publishAt = new Date(scheduleDateValue)
			if (Number.isNaN(publishAt.getTime())) {
				throw new Error("Ugyldigt datoformat.")
			}

			const response = await doFetch(`/eb-pristjek/articles/${scheduleTarget.articleId}/schedule`, {
				publishAt: publishAt.toISOString(),
			})
			const data = await response.json() as { item?: CreatedArticleLinks; error?: string }

			if (response.status >= 400 || !data?.item) {
				throw new Error(String(data?.error || "Kunne ikke planlægge artiklen."))
			}

			updateArticleInState(data.item)
			closeScheduleModal()
		} catch (err) {
			console.error("Fejl ved planlægning af artikel:", err)
			setScheduleError(err instanceof Error ? err.message : "Kunne ikke planlægge artiklen.")
		} finally {
			setIsScheduling(false)
		}
	}

	const handleUnschedule = async () => {
		if (!scheduleTarget) return
		setIsScheduling(true)
		setScheduleError("")

		try {
			const response = await doFetch(`/eb-pristjek/articles/${scheduleTarget.articleId}/schedule`, {
				status: "draft",
			})
			const data = await response.json() as { item?: CreatedArticleLinks; error?: string }

			if (response.status >= 400 || !data?.item) {
				throw new Error(String(data?.error || "Kunne ikke flytte artiklen tilbage til kladde."))
			}

			updateArticleInState(data.item)
			closeScheduleModal()
		} catch (err) {
			console.error("Fejl ved nulstilling af planlagt artikel:", err)
			setScheduleError(err instanceof Error ? err.message : "Kunne ikke flytte artiklen tilbage til kladde.")
		} finally {
			setIsScheduling(false)
		}
	}

	const handleSubmit = async () => {
		if (!canSubmit) return

		setActiveOperation("submit")
		setOperationStartedAt(Date.now())
		setIsSubmitting(true)
		setError("")
		setCreatedArticle(null)

		try {
			const normalizedArticleLinks = normalizeArticleLinksForRequest(articleLinks)
			const editorialProductSections = selectedProducts.map((item) => ({
				...item,
				sectionTitle: String(item.sectionTitle || item.productTitle || "").trim(),
			}))
			const requestBody = {
				articleType,
				singleOfferMode: articleType === "product-comparison" ? singleOfferMode : false,
				articleLength,
				topic: preview?.topic || "",
				title: editableTitle.trim() || preview?.title || "",
				aiDescription: prompt.trim(),
				customPrompt: prompt.trim(),
				featuredImageUrl: imageDataUrl,
				articleLinks: normalizedArticleLinks,
				discoverOptimized,
				...(articleType === "store-guide"
					? { storeSections: selectedStores, hasExplicitStoreSelection: true }
					: articleType === "editorial-guide"
						? { productSections: editorialProductSections, hasExplicitProductSelection: true, storeSections: selectedStores, hasExplicitStoreSelection: true }
						: { productSections: selectedProducts }),
			}

			const response = await doFetch("/eb-pristjek/articles/drafts", {
				...requestBody,
			})

			const data = await response.json() as ArticleDraftResponse | { error?: string }
			if (response.status >= 400) {
				throw new Error(String((data as { error?: string })?.error || "Kunne ikke oprette artiklen."))
			}

			const articleData = data as ArticleDraftResponse

			const articleId = Number(articleData.articleId || articleData.createdPost?.id || 0)
			if (!articleId) {
				throw new Error("API'et returnerede ikke et article id.")
			}

			const links = buildArticleLinks(articleId)
			const created = {
				articleId,
				previewLink: String(articleData.previewLink || links.previewLink),
				editLink: String(articleData.editLink || links.editLink),
				title: String(articleData.draft?.title || articleData.request?.title || ""),
				topic: String(articleData.draft?.topic || articleData.request?.topic || ""),
				articleTypeName: String(articleData.articleTypeName || getArticleTypeLabel(articleType)),
				status: "draft",
				date: new Date().toISOString(),
				dateGmt: new Date().toISOString(),
			}

			setCreatedArticle(created)
			setPreview(null)
			setEditableTitle("")
			setSelectedPreviewIds([])
			resetManualSearch()
			updateArticleInState(created)
			await loadArticleHistory()
		} catch (err) {
			console.error("Fejl ved oprettelse af artikel:", err)
			setError(err instanceof Error ? err.message : "Kunne ikke oprette artiklen.")
		} finally {
			setIsSubmitting(false)
			setActiveOperation(null)
			setOperationStartedAt(null)
		}
	}

	return (
		<Row className="mt-3 g-3">
			<Col xl={6}>
				<Card className="border-0 shadow-sm">
					<Card.Body>
						<h5 className="mb-3">Opret artikel</h5>
						<p className="text-muted mb-4">
							{articleType === "travel-article"
								? "Upload en TSV-fil med rejsetilbud, vælg det bedste tilbud og kontrollér oplysningerne før artikeloprettelsen."
								: "Upload et billede og skriv en prompt. Først får du et forslag til overskrift og valglister, og derefter vælger du selv hvad der skal med i artiklen."}
						</p>

						<Row className="g-3 align-items-start mb-3">
							<Col md={articleType === "travel-article" ? 12 : 6}>
								<Form.Group controlId="ebArticleType">
									<Form.Label>Artikeltype</Form.Label>
									<Form.Select value={articleType} onChange={(event) => {
										setArticleType(event.target.value)
										if (event.target.value !== "product-comparison") setSingleOfferMode(false)
										setPreview(null)
										setEditableTitle("")
										setSelectedPreviewIds([])
									}}>
										{ARTICLE_TYPES.map((type) => (
											<option key={type.value} value={type.value}>{type.label}</option>
										))}
									</Form.Select>
								</Form.Group>
							</Col>
							{articleType !== "travel-article" ? (
								<Col md={6}>
									<Form.Group controlId="ebArticleLength">
										<Form.Label>Artikellængde</Form.Label>
										<Form.Select value={articleLength} onChange={(event) => setArticleLength(event.target.value)}>
											{ARTICLE_LENGTH_OPTIONS.map((option) => (
												<option key={option.value} value={option.value}>{option.label}</option>
											))}
										</Form.Select>
									</Form.Group>
								</Col>
							) : null}
						</Row>

						{articleType === "travel-article" ? (
							<TravelArticleWorkflow
								doFetch={doFetch}
								onArticleCreated={(article) => {
									const created: CreatedArticleLinks = {
										...article,
										status: "draft",
										date: new Date().toISOString(),
										dateGmt: new Date().toISOString(),
									}
									setCreatedArticle(created)
									updateArticleInState(created)
									void loadArticleHistory()
								}}
							/>
						) : (
							<>
						<Row className="g-3 align-items-start mb-3">
							<Col md={6}>
								<Form.Group controlId="ebDiscoverOptimized" className="mb-0">
									<Form.Check
										type="checkbox"
										label="Discover-optimeret vinkel"
										checked={discoverOptimized}
										onChange={(event) => setDiscoverOptimized(event.target.checked)}
									/>
									<Form.Text className="text-muted">
										{articleType === "store-guide"
											? "Mere Discover-præget hook og vinkel."
											: isSingleOfferProductComparison
												? "Skærper hook og produktfokus."
												: "Mere Discover-præget hook og struktur."}
									</Form.Text>
								</Form.Group>
							</Col>
							{articleType === "product-comparison" ? (
								<Col md={6}>
									<Form.Group controlId="ebSingleOfferMode" className="mb-0">
										<Form.Check
											type="checkbox"
											label="Single product-mode"
											checked={singleOfferMode}
											onChange={(event) => {
												setSingleOfferMode(event.target.checked)
												setPreview(null)
												setEditableTitle("")
												setSelectedPreviewIds([])
												resetManualSearch()
											}}
										/>
									</Form.Group>
								</Col>
							) : null}
						</Row>

						<Form.Group className="mb-3" controlId="ebArticleImage">
							<Form.Label>Billede</Form.Label>
							<Form.Control type="file" accept="image/*" onChange={handleImageChange} />
							<Form.Text className="text-muted">
								{imageFileName ? `Valgt fil: ${imageFileName}` : "Upload et artikelbillede."}
							</Form.Text>
						</Form.Group>

						<Form.Group className="mb-3" controlId="ebArticlePrompt">
							<Form.Label>Prompt</Form.Label>
							<Form.Control
								as="textarea"
								rows={8}
								value={prompt}
								onChange={(event) => setPrompt(event.target.value)}
								placeholder={articleType === "store-guide"
									? (discoverOptimized
										? "fx Derfor giver det mening at kigge ekstra på jakkesætudvalg lige nu, hvis du skal til bryllup"
										: "fx Skriv en guide om valg af jakkesæt til et bryllup")
									: isSingleOfferProductComparison
										? (discoverOptimized
											? "fx Billige elcykler under 25.000 kr. med fokus på de modeller, der giver mest mening lige nu"
											: "fx Skriv en produktsammenligning med single-offer blokke om gode robotstøvsugere lige nu, og fremhæv produkterne tidligt")
									: articleType === "editorial-guide"
										? (discoverOptimized
											? "fx Mange overser denne løsning, når de vil slippe for mos og alger på fliser"
											: "fx Skriv en guide til smart tags, hvad man skal kigge efter og hvor man kan starte sin søgning")
										: (discoverOptimized
											? "fx Denne type støvsuger bliver valgt oftere nu, og her er hvorfor"
											: "fx Skriv en produktsammenligning af kabinekufferter til storbyferier og korte rejser")}
							/>
							<Form.Text className="text-muted">
								{articleType === "store-guide"
									? (discoverOptimized
										? 'Systemet laver titel og vinkel. Du vælger selv butikkerne fra listen med aktive forretninger.'
										: 'Du vælger selv butikkerne fra listen med aktive forretninger eller via manuel søgning.')
									: isSingleOfferProductComparison
										? (discoverOptimized
											? 'Systemet laver titel og vinkel. Du vælger selv produkter fra de nyeste produkter eller via manuel søgning.'
											: 'Brug prompten til at beskrive vinkel, behov og tone. Du vælger selv produkterne før artiklen bygges.')
									: articleType === "editorial-guide"
										? (discoverOptimized
											? 'Når boksen er slået til, prøver systemet at skrive en mere aktuel Discover-vinkel. Du vælger selv produkter og butikker bagefter.'
											: 'Du vælger selv produkter og butikker fra listerne eller via manuel søgning.')
										: (discoverOptimized
											? 'Når boksen er slået til, prøver systemet at gøre artiklen mere feed-/Discover-egnet. Du vælger selv produkterne.'
											: 'Du kan godt ændre prompten efter valglisterne er hentet. Klik kun igen, hvis du vil nulstille listen.')}
							</Form.Text>
						</Form.Group>

						<div className="mb-3 p-3 border rounded bg-light-subtle">
							<div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-2">
								<Form.Label className="mb-0">Ekstra artikel-links (valgfrit)</Form.Label>
								<Button
									variant="outline-secondary"
									size="sm"
									onClick={() => setArticleLinks((prev) => [...prev, createEmptyArticleInputLink()])}
								>
									Tilføj link
								</Button>
							</div>
							<div className="d-flex flex-column gap-2">
								{articleLinks.map((link, index) => (
									<Row key={`article-link-${index}`} className="g-2 align-items-end">
										<Col md={6}>
											<Form.Group controlId={`articleLinkHref-${index}`}>
												<Form.Label className="small mb-1">URL</Form.Label>
												<Form.Control
													type="url"
													value={link.href}
													onChange={(event) => setArticleLinks((prev) => prev.map((item, itemIndex) => itemIndex === index ? { ...item, href: event.target.value } : item))}
												/>
											</Form.Group>
										</Col>
										<Col md={5}>
											<Form.Group controlId={`articleLinkText-${index}`}>
												<Form.Label className="small mb-1">Ankertekst</Form.Label>
												<Form.Control
													type="text"
													value={link.text}
													onChange={(event) => setArticleLinks((prev) => prev.map((item, itemIndex) => itemIndex === index ? { ...item, text: event.target.value } : item))}
												/>
											</Form.Group>
										</Col>
										<Col md={1}>
											<div className="d-grid">
												<Button
													variant="outline-danger"
													size="sm"
													disabled={articleLinks.length === 1}
													onClick={() => setArticleLinks((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}
												>
													-
												</Button>
											</div>
										</Col>
									</Row>
								))}
							</div>
						</div>

						<div className="d-flex flex-wrap gap-2">
							<Button variant="primary" disabled={!canDiscover || isDiscovering || isSubmitting} onClick={handleDiscover}>
								{isDiscovering ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" />
										Henter valglister...
									</>
								) : (
									"Hent valglister"
								)}
							</Button>

							{preview ? (
								<>
									<Button variant="success" disabled={!canSubmit || isSubmitting} onClick={handleSubmit}>
										{isSubmitting ? (
											<>
												<Spinner animation="border" size="sm" className="me-2" />
												Opretter artikel...
											</>
										) : (
											articleType === "store-guide"
												? "Byg artikel med valgte butikker"
												: isSingleOfferProductComparison
													? "Byg produktsammenligning"
												: articleType === "editorial-guide"
													? "Byg guide med valgte produkter og butikker"
													: "Byg artikel med valgte produkter"
										)}
									</Button>
									<Button variant="outline-secondary" disabled={isDiscovering || isSubmitting} onClick={handleStartOver}>
										Start forfra
									</Button>
								</>
							) : null}
						</div>

						{operationStatus ? (
							<Alert variant="info" className="mt-3 mb-0">
								<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
									<strong>{operationStatus.currentStep.label}</strong>
									<span className="small text-muted">Kørt i {operationStatus.elapsedLabel}</span>
								</div>
								<div className="small mb-2">{operationStatus.currentStep.description}</div>
								<ProgressBar now={operationStatus.currentStep.progress} animated striped label={`${operationStatus.currentStep.progress}%`} />
								<div className="small text-muted mt-2">
									{operationStatus.mode === "discover"
										? "Valglisterne hentes uden automatisk produkt- eller butiksudvælgelse."
										: "Oprettelse kan tage lidt tid, fordi både indhold, billede og WordPress-data skal på plads."}
								</div>
							</Alert>
						) : null}

						{error ? <Alert variant="danger" className="mt-3 mb-0">{error}</Alert> : null}

						{preview ? (
							<div className="mt-4 pt-3 border-top">
								<div className="mb-3">
									<h6 className="mb-1">Foreslået artikel</h6>
									<div className="text-muted small mb-2">Du kan redigere titlen før artiklen bygges.</div>
									<Form.Control
										type="text"
										value={editableTitle}
										onChange={(event) => setEditableTitle(event.target.value)}
										placeholder="Artikeloverskrift"
									/>
									<div className="text-muted small mt-2">Topic: {preview.topic || "-"}</div>
								</div>

								<div className="d-flex flex-column gap-2" style={selectionListContainerStyle}>
									{articleType === "editorial-guide" ? (
										<>
											<Row className="g-3">
												<Col md={6}>
													<div className="border rounded p-3 bg-white h-100" style={selectionPanelStyle}>
															<div className="small text-muted fw-semibold">Produkter</div>
														<div className="d-flex flex-column gap-2 mt-2" style={selectionListContainerStyle}>
															{selectedProductItems.map((item) => (
																<label key={item.id} className="border rounded bg-light" style={selectionItemStyle}>
																	<div className="d-flex gap-2 align-items-center">
																		<Form.Check type="checkbox" className="mt-0" checked={true} onChange={() => togglePreviewSelection(item.id)} />
																		<div className="flex-grow-1 small">
																			<div className="fw-semibold" style={selectionLabelStyle}>{item.label}</div>
																			{getSelectionItemDescription(item) ? <div className="text-muted">{getSelectionItemDescription(item)}</div> : null}
																		</div>
																	</div>
																</label>
															))}
																<div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mt-2">
																	<div className="small text-muted fw-semibold">{isManualProductSearchActive ? "Søgeresultater" : "Produktkandidater"}</div>
																	<Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => manualProductSearchOpen ? resetManualCandidateSearch("product") : setManualProductSearchOpen(true)}>
																		{manualProductSearchOpen ? "Vis forslag" : "Søg manuelt"}
																	</Button>
																</div>
																{manualProductSearchOpen ? (
																	<div className="border rounded p-2 bg-light-subtle">
																		<div className="d-flex gap-2">
																			<Form.Control
																				size="sm"
																				value={manualProductSearchQuery}
																				onChange={(event) => {
																					setManualProductSearchQuery(event.target.value)
																					if (!event.target.value.trim()) {
																						setManualProductSearchResults([])
																						setManualProductSearchError("")
																					}
																				}}
																				onKeyDown={(event) => {
																					if (event.key === "Enter") {
																						event.preventDefault()
																						void handleManualCandidateSearch("product")
																					}
																				}}
																				placeholder="Søg efter produkt"
																			/>
																			<Button size="sm" variant="outline-secondary" disabled={isManualProductSearchLoading || !manualProductSearchQuery.trim()} onClick={() => handleManualCandidateSearch("product")}>
																				{isManualProductSearchLoading ? "Søger..." : "Søg"}
																			</Button>
																		</div>
																		{manualProductSearchError ? <div className="small text-danger mt-2">{manualProductSearchError}</div> : null}
																	</div>
																) : null}
																{(isManualProductSearchActive ? visibleManualProductSearchResults : candidateProductItems).map((item) => (
																<label key={item.id} className="border rounded" style={selectionItemStyle}>
																	<div className="d-flex gap-2 align-items-center">
																			<Form.Check type="checkbox" className="mt-0" checked={selectedPreviewIds.includes(item.id)} onChange={() => toggleCandidateSelection(item)} />
																		<div className="flex-grow-1 small">
																			<div className="fw-semibold" style={selectionLabelStyle}>{item.label}</div>
																			{getSelectionItemDescription(item) ? <div className="text-muted">{getSelectionItemDescription(item)}</div> : null}
																		</div>
																	</div>
																</label>
															))}
														</div>
													</div>
												</Col>
												<Col md={6}>
													<div className="border rounded p-3 bg-white h-100" style={selectionPanelStyle}>
															<div className="small text-muted fw-semibold">Butikker</div>
														<div className="d-flex flex-column gap-2 mt-2" style={selectionListContainerStyle}>
															{selectedStoreItems.map((item) => (
																<label key={item.id} className="border rounded bg-light" style={selectionItemStyle}>
																	<div className="d-flex gap-2 align-items-center">
																		<Form.Check type="checkbox" className="mt-0" checked={true} onChange={() => togglePreviewSelection(item.id)} />
																		<div className="flex-grow-1 small">
																			<div className="fw-semibold" style={selectionLabelStyle}>{item.label}</div>
																			{getSelectionItemDescription(item) ? <div className="text-muted">{getSelectionItemDescription(item)}</div> : null}
																		</div>
																	</div>
																</label>
															))}
																<div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mt-2">
																	<div className="small text-muted fw-semibold">{isManualStoreSearchActive ? "Søgeresultater" : "Butikskandidater"}</div>
																	<Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => manualStoreSearchOpen ? resetManualCandidateSearch("store") : setManualStoreSearchOpen(true)}>
																		{manualStoreSearchOpen ? "Vis forslag" : "Søg manuelt"}
																	</Button>
																</div>
																{manualStoreSearchOpen ? (
																	<div className="border rounded p-2 bg-light-subtle">
																		<div className="d-flex gap-2">
																			<Form.Control
																				size="sm"
																				value={manualStoreSearchQuery}
																				onChange={(event) => {
																					setManualStoreSearchQuery(event.target.value)
																					if (!event.target.value.trim()) {
																						setManualStoreSearchResults([])
																						setManualStoreSearchError("")
																					}
																				}}
																				onKeyDown={(event) => {
																					if (event.key === "Enter") {
																						event.preventDefault()
																						void handleManualCandidateSearch("store")
																					}
																				}}
																				placeholder="Søg efter butik"
																			/>
																			<Button size="sm" variant="outline-secondary" disabled={isManualStoreSearchLoading || !manualStoreSearchQuery.trim()} onClick={() => handleManualCandidateSearch("store")}>
																				{isManualStoreSearchLoading ? "Søger..." : "Søg"}
																			</Button>
																		</div>
																		{manualStoreSearchError ? <div className="small text-danger mt-2">{manualStoreSearchError}</div> : null}
																	</div>
																) : null}
																{displayedStoreCandidateItems.map((item) => (
																<label key={item.id} className="border rounded" style={selectionItemStyle}>
																	<div className="d-flex gap-2 align-items-center">
																			<Form.Check type="checkbox" className="mt-0" checked={selectedPreviewIds.includes(item.id)} onChange={() => toggleCandidateSelection(item)} />
																		<div className="flex-grow-1 small">
																			<div className="fw-semibold" style={selectionLabelStyle}>{item.label}</div>
																			{getSelectionItemDescription(item) ? <div className="text-muted">{getSelectionItemDescription(item)}</div> : null}
																		</div>
																	</div>
																</label>
															))}
														</div>
													</div>
												</Col>
											</Row>
										</>
									) : (
										<>
											<div className="small text-muted fw-semibold">{articleType === "store-guide" ? "Valgte butikker" : "Valgte produkter"}</div>
											{selectedPreviewItems.map((item) => (
										<label key={item.id} className="border rounded bg-light" style={selectionItemStyle}>
											<div className="d-flex gap-2 align-items-center">
												<Form.Check
													type="checkbox"
													className="mt-0"
													checked={true}
													onChange={() => togglePreviewSelection(item.id)}
												/>
												<div className="flex-grow-1 small">
																	<div className="fw-semibold" style={selectionLabelStyle}>{item.label}</div>
																	{getSelectionItemDescription(item) ? <div className="text-muted">{getSelectionItemDescription(item)}</div> : null}
												</div>
											</div>
										</label>
											))}

													{(articleType === "store-guide" ? shouldShowStoreCandidateSection : shouldShowProductCandidateSection) ? (
												<>
																	<div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mt-2">
																		<div className="small text-muted fw-semibold">{(articleType === "store-guide" ? isManualStoreSearchActive : isManualProductSearchActive) ? "Søgeresultater" : "Kandidater"}</div>
																		<Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => articleType === "store-guide" ? (manualStoreSearchOpen ? resetManualCandidateSearch("store") : setManualStoreSearchOpen(true)) : (manualProductSearchOpen ? resetManualCandidateSearch("product") : setManualProductSearchOpen(true))}>
																			{(articleType === "store-guide" ? manualStoreSearchOpen : manualProductSearchOpen) ? "Vis forslag" : "Søg manuelt"}
																		</Button>
																	</div>
																	{articleType === "store-guide" && manualStoreSearchOpen ? (
																		<div className="border rounded p-2 bg-light-subtle mb-2">
																			<div className="d-flex gap-2">
																				<Form.Control size="sm" value={manualStoreSearchQuery} onChange={(event) => {
																					setManualStoreSearchQuery(event.target.value)
																					if (!event.target.value.trim()) {
																						setManualStoreSearchResults([])
																						setManualStoreSearchError("")
																					}
																				}} onKeyDown={(event) => {
																					if (event.key === "Enter") {
																						event.preventDefault()
																						void handleManualCandidateSearch("store")
																					}
																				}} placeholder="Søg efter butik" />
																				<Button size="sm" variant="outline-secondary" disabled={isManualStoreSearchLoading || !manualStoreSearchQuery.trim()} onClick={() => handleManualCandidateSearch("store")}>{isManualStoreSearchLoading ? "Søger..." : "Søg"}</Button>
																			</div>
																			{manualStoreSearchError ? <div className="small text-danger mt-2">{manualStoreSearchError}</div> : null}
																		</div>
																	) : null}
																	{articleType !== "store-guide" && manualProductSearchOpen ? (
																		<div className="border rounded p-2 bg-light-subtle mb-2">
																			<div className="d-flex gap-2">
																				<Form.Control size="sm" value={manualProductSearchQuery} onChange={(event) => {
																					setManualProductSearchQuery(event.target.value)
																					if (!event.target.value.trim()) {
																						setManualProductSearchResults([])
																						setManualProductSearchError("")
																					}
																				}} onKeyDown={(event) => {
																					if (event.key === "Enter") {
																						event.preventDefault()
																						void handleManualCandidateSearch("product")
																					}
																				}} placeholder="Søg efter produkt" />
																				<Button size="sm" variant="outline-secondary" disabled={isManualProductSearchLoading || !manualProductSearchQuery.trim()} onClick={() => handleManualCandidateSearch("product")}>{isManualProductSearchLoading ? "Søger..." : "Søg"}</Button>
																			</div>
																			{manualProductSearchError ? <div className="small text-danger mt-2">{manualProductSearchError}</div> : null}
																		</div>
																	) : null}
																	{(articleType === "store-guide" ? displayedStoreCandidateItems : displayedProductCandidateItems).map((item) => (
												<label key={item.id} className="border rounded" style={selectionItemStyle}>
													<div className="d-flex gap-2 align-items-center">
														<Form.Check
															type="checkbox"
															className="mt-0"
																			checked={selectedPreviewIds.includes(item.id)}
																			onChange={() => toggleCandidateSelection(item)}
														/>
														<div className="flex-grow-1 small">
																			<div className="fw-semibold" style={selectionLabelStyle}>{item.label}</div>
																			{getSelectionItemDescription(item) ? <div className="text-muted">{getSelectionItemDescription(item)}</div> : null}
														</div>
													</div>
												</label>
														))}
													</>
												) : null}
											</>
										)}
								</div>

									{selectedPreviewItems.length < 1 ? (
									<Alert variant="warning" className="mt-3 mb-0">
											{articleType === "store-guide"
												? "Der skal være mindst 1 valgt butik, før artiklen kan bygges."
												: isSingleOfferProductComparison
													? "Der skal være mindst 1 valgt produkt, før denne produktsammenligning kan bygges."
												: articleType === "editorial-guide"
													? "Der skal være mindst 1 valgt produkt eller butik, før guiden kan bygges."
													: "Der skal være mindst 1 valgt produkt, før artiklen kan bygges."}
									</Alert>
								) : null}
							</div>
						) : null}
							</>
						)}
					</Card.Body>
				</Card>
			</Col>

			<Col xl={6}>
				<Card className="border-0 shadow-sm h-100">
					<Card.Body>
						<div className="d-flex justify-content-between align-items-center mb-3">
							<h5 className="mb-0">Artikelplan</h5>
							{createdArticle ? (
								<span className="text-success fw-semibold">Ny kladde oprettet</span>
							) : null}
						</div>
						<div className="d-flex flex-wrap gap-2 mb-3">
							<Badge bg="secondary">Kladder: {plannerCounts.draft}</Badge>
							<Badge bg="warning" text="dark">Planlagte: {plannerCounts.future}</Badge>
							<Badge bg="success">Publicerede: {plannerCounts.publish}</Badge>
						</div>
						{createdArticle ? (
							<>
								<Alert variant="success" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
									<span>Artiklen blev oprettet som kladde med ID {createdArticle.articleId}.</span>
									<span className="fw-semibold">{createdArticle.articleTypeName}</span>
								</Alert>

								<div className="border rounded p-3 mb-3 bg-light">
									<div className="mb-2"><strong>Titel:</strong> {createdArticle.title || "-"}</div>
									<div className="mb-2"><strong>Topic:</strong> {createdArticle.topic || "-"}</div>
									<div><strong>Article ID:</strong> {createdArticle.articleId}</div>
								</div>

								<div className="d-flex flex-column gap-2">
									{canShowPreviewLink(createdArticle) ? (
										<Button as="a" href={createdArticle.previewLink} target="_blank" rel="noopener noreferrer" variant="outline-primary">
											Åbn preview i nyt vindue
										</Button>
									) : null}
									<Button as="a" href={createdArticle.editLink} target="_blank" rel="noopener noreferrer" variant="outline-secondary">
										Åbn edit i WordPress
									</Button>
									<Button variant="outline-dark" onClick={() => openScheduleModal(createdArticle)}>
										Planlæg publicering
									</Button>
								</div>
							</>
						) : (
							<div className="text-muted">
								Når en artikel er oprettet, vises preview, edit og planlægning her.
							</div>
						)}

						<div className="mt-4 pt-3 border-top">
							<div className="d-flex flex-column gap-3 mb-3">
								<div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
									<div>
										<h6 className="mb-1">Oversigt og kalender</h6>
									<div className="text-muted small">Der indhentes kun artikler fra de seneste {HISTORY_WINDOW_MONTHS} måneder.</div>
									</div>
									<Button size="sm" variant="outline-secondary" onClick={loadArticleHistory} disabled={isLoadingHistory}>
										{isLoadingHistory ? "Opdaterer..." : "Opdater"}
									</Button>
								</div>

								<div style={plannerViewSwitcherStyle}>
									<Button
										variant="light"
										onClick={() => setPlannerView("list")}
										style={{
											...plannerViewButtonBaseStyle,
											...(plannerView === "list" ? plannerViewButtonActiveStyle : {}),
										}}
									>
										<div className="fw-semibold mb-1">Listevisning</div>
										<div className="small text-muted">Alle artikler med statusfilter og hurtige handlinger.</div>
									</Button>
									<Button
										variant="light"
										onClick={() => setPlannerView("calendar")}
										style={{
											...plannerViewButtonBaseStyle,
											...(plannerView === "calendar" ? plannerViewButtonActiveStyle : {}),
										}}
									>
										<div className="fw-semibold mb-1">Kalendervisning</div>
										<div className="small text-muted">Kun planlagte og publicerede artikler for bedre overblik.</div>
									</Button>
								</div>

								<div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={plannerToolbarStyle}>
									{plannerView === "list" ? (
										<>
											<div>
												<div className="fw-semibold small">Listefilter</div>
												<div className="text-muted small">Vælg hvilke statuser der skal vises i listen.</div>
											</div>
											<Form.Select size="sm" style={{ maxWidth: "220px" }} value={plannerFilter} onChange={(event) => setPlannerFilter(event.target.value as "all" | "draft" | "future" | "publish")}>
												<option value="all">Alle statuser</option>
												<option value="draft">Kun kladder</option>
												<option value="future">Kun planlagte</option>
												<option value="publish">Kun publicerede</option>
											</Form.Select>
										</>
									) : (
										<div>
											<div className="fw-semibold small">Kalenderfilter</div>
											<div className="text-muted small">Kalenderen viser altid kun planlagte og publicerede artikler.</div>
										</div>
									)}
								</div>
							</div>
							{isLoadingHistory ? (
								<div className="text-muted d-flex align-items-center gap-2">
									<Spinner animation="border" size="sm" />
									Henter artikeloversigt...
								</div>
							) : historyError ? (
								<Alert variant="warning" className="mb-0">{historyError}</Alert>
							) : (plannerView === "calendar" ? calendarArticleHistory.length : filteredArticleHistory.length) ? (
								plannerView === "list" ? (
									<div className="d-flex flex-column gap-2" style={plannerListContainerStyle}>
										{filteredArticleHistory.map((item) => (
											<div key={item.articleId} className="border rounded p-3 bg-white">
												<div className="d-flex justify-content-between align-items-start gap-2 mb-2">
													<div className="flex-grow-1">
														<div className="fw-semibold">{item.title || `Artikel ${item.articleId}`}</div>
														<div className="text-muted small">ID {item.articleId}{item.topic ? ` · ${item.topic}` : ""}</div>
													</div>
													<Badge bg={getPlannerStatusVariant(item.status)} text={item.status === "future" ? "dark" : undefined}>{getPlannerStatusLabel(item.status)}</Badge>
												</div>
												<div className="small text-muted mb-2">{getArticlePrimaryDateLabel(item)}</div>
												<div className="d-flex flex-wrap gap-2">
													{canShowPreviewLink(item) ? <Button as="a" href={item.previewLink} target="_blank" rel="noopener noreferrer" size="sm" variant="outline-primary">Preview</Button> : null}
													<Button as="a" href={item.editLink} target="_blank" rel="noopener noreferrer" size="sm" variant="outline-secondary">Edit</Button>
													{canShowPublicLink(item) ? <Button as="a" href={item.publicLink} target="_blank" rel="noopener noreferrer" size="sm" variant="outline-success">Vis live</Button> : null}
													{canCopyPublicLink(item) ? (
														<Button
															size="sm"
															variant="outline-success"
															onClick={() => handleCopyPublicLink(item)}
															aria-label={`Kopiér link til ${item.title || `artikel ${item.articleId}`}`}
														>
															{copiedArticleId === item.articleId ? "Link kopieret" : copyErrorArticleId === item.articleId ? "Kunne ikke kopiere" : "Kopiér link"}
														</Button>
													) : null}
													<Button size="sm" variant="outline-dark" onClick={() => openScheduleModal(item)}>{item.status === "future" ? "Rediger plan" : "Planlæg"}</Button>
												</div>
											</div>
										))}
									</div>
								) : (
									<>
										<div style={calendarToolbarStyle}>
											<Button
												size="sm"
												variant="outline-secondary"
												onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
												style={calendarNavButtonStyle}
											>
												&lt;
											</Button>
											<div className="text-center flex-grow-1">
												<div style={calendarMonthLabelStyle}>{calendarMonth.toLocaleDateString("da-DK", { month: "long", year: "numeric" })}</div>
												<div className="text-muted" style={{ fontSize: "0.76rem" }}>Planlagte/publicerede, hentet 3 måneder tilbage</div>
											</div>
											<Button
												size="sm"
												variant="outline-secondary"
												onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
												style={calendarNavButtonStyle}
											>
												&gt;
											</Button>
										</div>
										<div style={calendarGridStyle}>
											{calendarDays.map((day) => {
												const dayKey = getCalendarKey(day)
												const items = calendarItemsByDay.get(dayKey) || []
												const inCurrentMonth = day.getMonth() === calendarMonth.getMonth()
												const isToday = dayKey === todayCalendarKey
												const isSelected = dayKey === selectedCalendarDayKey
												const isExpanded = isSelected && items.length > 0

												return (
													<div key={dayKey} style={isExpanded ? calendarExpandedGridItemStyle : undefined}>
														<div
															role="button"
															tabIndex={0}
															onClick={() => handleCalendarDayToggle(dayKey)}
															onKeyDown={(event) => {
																if (event.key === "Enter" || event.key === " ") {
																	event.preventDefault()
																	handleCalendarDayToggle(dayKey)
																}
															}}
															style={{
																...calendarCellBaseStyle,
																...(!inCurrentMonth ? calendarCellMutedStyle : {}),
																...(isToday ? calendarCellTodayStyle : {}),
																...(isSelected ? calendarCellSelectedStyle : {}),
																...(isToday && isSelected ? calendarCellTodaySelectedStyle : {}),
																...(isExpanded ? calendarCellExpandedStyle : {}),
															}}
														>
															<div className="d-flex justify-content-between align-items-center">
																<div>
																	<div className={`fw-semibold ${inCurrentMonth ? "text-dark" : "text-muted"}`} style={{ fontSize: "0.95rem" }}>{day.getDate()}</div>
																	<div className="text-muted" style={{ fontSize: "0.76rem" }}>{day.toLocaleDateString("da-DK", { month: "short" })}</div>
																</div>
																{items.length ? <Badge bg="dark">{items.length}</Badge> : null}
															</div>
															<div className="d-flex flex-column gap-2">
																{items.length ? (
																	<>
																		<div className="fw-semibold" style={{ fontSize: "0.84rem", lineHeight: 1.2 }}>{items.length} {items.length === 1 ? "artikel" : "artikler"}</div>
																		<div style={calendarDotRowStyle}>
																			{items.slice(0, 5).map((item) => (
																				<span key={item.articleId} style={{ ...calendarDotBaseStyle, background: getCalendarStatusDotColor(item.status) }} />
																			))}
																			{items.length > 5 ? <span style={calendarDayMetaStyle}>+{items.length - 5}</span> : null}
																		</div>
																		{isExpanded ? (
																			<div style={calendarExpandedListStyle}>
																				{items.map((item) => (
																					<button
																						key={item.articleId}
																						type="button"
																						className="btn btn-sm text-start"
																						style={calendarExpandedArticleButtonStyle}
																						onClick={(event) => {
																							event.stopPropagation()
																							openScheduleModal(item)
																						}}
																					>
																						<div className="d-flex justify-content-between align-items-start gap-2">
																							<div className="flex-grow-1">
																								<div className="fw-semibold" style={{ fontSize: "0.76rem", whiteSpace: "normal", overflowWrap: "anywhere" }}>{item.title || `Artikel ${item.articleId}`}</div>
																								<div className="text-muted" style={{ fontSize: "0.7rem", marginTop: "0.18rem" }}>{getCalendarArticleTimeLabel(item)}</div>
																							</div>
																							<span style={{ ...calendarDotBaseStyle, background: getCalendarStatusDotColor(item.status), flexShrink: 0, marginTop: "0.18rem" }} />
																						</div>
																					</button>
																				))}
																			</div>
																		) : null}
																	</>
																) : null}
															</div>
														</div>
													</div>
												)
											})}
										</div>
									</>
								)
							) : (
								<div className="text-muted">{plannerView === "calendar" ? "Ingen planlagte eller publicerede artikler i den valgte periode endnu." : "Ingen artikler matcher det valgte filter endnu."}</div>
							)}
						</div>
					</Card.Body>
				</Card>
			</Col>

			<Modal show={Boolean(scheduleTarget)} onHide={closeScheduleModal} centered>
				<Modal.Header closeButton>
					<Modal.Title>{isScheduleReadOnly ? "Artikelinformation" : "Planlæg publicering"}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mb-3">
						<div className="fw-semibold">{scheduleTarget?.title || (scheduleTarget ? `Artikel ${scheduleTarget.articleId}` : "")}</div>
						<div className="text-muted small">{scheduleTarget ? getArticlePrimaryDateLabel(scheduleTarget) : ""}</div>
					</div>
					{isScheduleReadOnly ? (
						<div className="border rounded p-3 bg-light">
							<div className="small text-muted mb-2">Denne artikel er allerede publiceret og kan kun vises som information her.</div>
							<div className="small"><strong>Status:</strong> {getPlannerStatusLabel(scheduleTarget?.status)}</div>
							<div className="small mt-1"><strong>Publiceret:</strong> {scheduleTarget?.publishedAt ? formatPlannerDate(scheduleTarget.publishedAt, true) : "-"}</div>
							<div className="small mt-1"><strong>Article ID:</strong> {scheduleTarget?.articleId || "-"}</div>
						</div>
					) : (
						<Form.Group controlId="schedulePublishAt">
							<Form.Label>Publish dato og klokkeslæt</Form.Label>
							<Form.Control type="datetime-local" value={scheduleDateValue} onChange={(event) => setScheduleDateValue(event.target.value)} />
							<Form.Text className="text-muted">Vælg det præcise tidspunkt, hvor artiklen skal gå live.</Form.Text>
						</Form.Group>
					)}
					{scheduleError ? <Alert variant="danger" className="mt-3 mb-0">{scheduleError}</Alert> : null}
				</Modal.Body>
				<Modal.Footer className="d-flex justify-content-between">
					{isScheduleReadOnly ? (
						<div className="ms-auto d-flex gap-2">
							<Button variant="outline-secondary" onClick={closeScheduleModal}>Luk</Button>
							{canShowPublicLink(scheduleTarget) ? <Button as="a" href={scheduleTarget?.publicLink} target="_blank" rel="noopener noreferrer" variant="outline-success">Vis live</Button> : null}
							{scheduleTarget && canCopyPublicLink(scheduleTarget) ? (
								<Button variant="outline-success" onClick={() => handleCopyPublicLink(scheduleTarget)}>
									{copiedArticleId === scheduleTarget.articleId ? "Link kopieret" : copyErrorArticleId === scheduleTarget.articleId ? "Kunne ikke kopiere" : "Kopiér link"}
								</Button>
							) : null}
							{scheduleTarget?.editLink ? <Button as="a" href={scheduleTarget.editLink} target="_blank" rel="noopener noreferrer" variant="outline-primary">Åbn i WordPress</Button> : null}
						</div>
					) : (
						<>
							<div className="d-flex gap-2">
								{scheduleTarget?.status === "future" ? <Button variant="outline-secondary" onClick={handleUnschedule} disabled={isScheduling}>Flyt tilbage til kladde</Button> : null}
								{scheduleTarget && canCopyPublicLink(scheduleTarget) ? (
									<Button variant="outline-success" onClick={() => handleCopyPublicLink(scheduleTarget)} disabled={isScheduling}>
										{copiedArticleId === scheduleTarget.articleId ? "Link kopieret" : copyErrorArticleId === scheduleTarget.articleId ? "Kunne ikke kopiere" : "Kopiér link"}
									</Button>
								) : null}
							</div>
							<div className="d-flex gap-2">
								<Button variant="outline-secondary" onClick={closeScheduleModal} disabled={isScheduling}>Luk</Button>
								<Button variant="primary" onClick={handleScheduleSubmit} disabled={isScheduling}>
									{isScheduling ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" />
											Gemmer...
										</>
									) : "Gem publish-plan"}
								</Button>
							</div>
						</>
					)}
				</Modal.Footer>
			</Modal>
		</Row>
	)
}
